/**
 * Storage Adapter Interface
 *
 * Defines the contract for storage adapters that persist application data.
 * Supports both local storage (browser) and remote SQL storage (BFF server).
 *
 * Key Features:
 * - Generic key-value storage
 * - Optimistic locking with version control
 * - Conflict detection and resolution
 * - Type-safe operations
 *
 * @see LocalStorageAdapter - Browser localStorage implementation
 * @see SqlStorageAdapter - Remote SQL database via BFF server
 */

/**
 * Result type for operations that may fail
 */
export type Result<T, E = Error> = { success: true; data: T } | { success: false; error: E }

/**
 * Conflict information when optimistic locking fails
 */
export interface ConflictInfo<T> {
  /** Current version in storage */
  currentVersion: number
  /** Version that was attempted */
  attemptedVersion: number
  /** Current data in storage */
  currentData: T
  /** Data that was attempted to save */
  attemptedData: T
  /** Base version if available (for three-way merge) */
  baseData?: T
}

/**
 * Storage metadata
 */
export interface StorageMetadata {
  /** Unique identifier */
  id: string
  /** Version number for optimistic locking */
  version: number
  /** Last modified timestamp */
  lastModified: string
  /** User who last modified (if available) */
  modifiedBy?: string
  /** Organization ID (if applicable) */
  orgId?: string
}

/**
 * Stored data with metadata
 */
export interface StoredData<T> {
  /** The actual data */
  data: T
  /** Storage metadata */
  metadata: StorageMetadata
}

/**
 * Options for save operations
 */
export interface SaveOptions {
  /** Expected version for optimistic locking (undefined = don't check) */
  expectedVersion?: number
  /** User ID performing the operation */
  userId?: string
  /** Organization ID */
  orgId?: string
  /** Whether to force save (skip version check) */
  force?: boolean
}

/**
 * Options for list operations
 */
export interface ListOptions {
  /** Organization ID filter */
  orgId?: string
  /** User ID filter */
  userId?: string
  /** Maximum results to return */
  limit?: number
  /** Offset for pagination */
  offset?: number
  /** Sort field */
  sortBy?: string
  /** Sort direction */
  sortDirection?: 'asc' | 'desc'
}

/**
 * Storage adapter interface for persisting application data
 *
 * @template T The type of data being stored
 */
export interface IStorageAdapter<T> {
  /**
   * Get data by key
   *
   * @param key Unique identifier for the data
   * @returns Result with stored data or error
   */
  get(key: string): Promise<Result<StoredData<T> | null>>

  /**
   * Save data with optimistic locking
   *
   * @param key Unique identifier for the data
   * @param data Data to save
   * @param options Save options (version check, user info)
   * @returns Result with saved data or conflict error
   */
  save(key: string, data: T, options?: SaveOptions): Promise<Result<StoredData<T>, ConflictInfo<T>>>

  /**
   * Delete data by key
   *
   * @param key Unique identifier for the data
   * @param options Delete options (version check)
   * @returns Result indicating success or conflict
   */
  delete(key: string, options?: SaveOptions): Promise<Result<void, ConflictInfo<T>>>

  /**
   * List all keys (with optional filtering)
   *
   * @param options List options (org filter, pagination)
   * @returns Result with list of keys and metadata
   */
  list(options?: ListOptions): Promise<Result<StorageMetadata[]>>

  /**
   * Check if key exists
   *
   * @param key Unique identifier to check
   * @returns Result with boolean indicating existence
   */
  exists(key: string): Promise<Result<boolean>>

  /**
   * Clear all data (use with caution)
   *
   * @param options Clear options (org filter to limit scope)
   * @returns Result indicating success
   */
  clear(options?: { orgId?: string }): Promise<Result<void>>

  /**
   * Get adapter type identifier
   */
  readonly type: 'local' | 'sql'

  /**
   * Check if adapter is available/healthy
   */
  isAvailable(): Promise<boolean>
}

/**
 * Error types for storage operations
 */
export class StorageError extends Error {
  constructor(
    message: string,
    public readonly code: StorageErrorCode,
    public readonly cause?: unknown
  ) {
    super(message)
    this.name = 'StorageError'
  }
}

export enum StorageErrorCode {
  /** Key not found */
  NOT_FOUND = 'NOT_FOUND',
  /** Version conflict (optimistic locking) */
  CONFLICT = 'CONFLICT',
  /** Network error (for remote storage) */
  NETWORK_ERROR = 'NETWORK_ERROR',
  /** Authentication error (401 — token invalid/expired; triggers re-login) */
  AUTH_ERROR = 'AUTH_ERROR',
  /** Authorization error (403 — authenticated but not permitted; does NOT re-login) */
  FORBIDDEN = 'FORBIDDEN',
  /** Storage quota exceeded */
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',
  /** Invalid data format */
  INVALID_DATA = 'INVALID_DATA',
  /** Rate limited by server (429 Too Many Requests) */
  RATE_LIMITED = 'RATE_LIMITED',
  /** Server error (500+) on a mutation — not retried */
  SERVER_ERROR = 'SERVER_ERROR',
  /** Unknown error */
  UNKNOWN = 'UNKNOWN',
}
